import { useQuery } from '@tanstack/react-query';
import { Dayjs } from 'dayjs';
import {
    getLastForecastDeltaUploadDate,
    getTotalForecastDeltaValuesByDateRangeAndWorktype,
    getAllForecastDeltasByDateRangeWorktypeAndLocation,
    getSumOfAllForecastDeltaValuesByDateRangeWorktypeAndLocation,
    getAllForecastDeltasByDateRangeAndWorktype,
} from '../../services/forecastDelta';
import { QueryKey } from '../../enums/QueryKey';
import { formatLastUploadDate } from '../../utils/dateUtils';

export function useLastForecastDeltaUploadDate(worktype: number) {
    const { data } = useQuery({
        queryKey: [QueryKey.LastUploadDate + '-forecastDelta', { worktype }],
        queryFn: () => getLastForecastDeltaUploadDate(worktype),
        enabled: !!worktype,
    });

    return formatLastUploadDate(data);
}

export function useTotalForecastDeltasByDateRangeAndWorktype({
    dateFrom,
    dateTo,
    worktype,
}: {
    dateFrom: Dayjs;
    dateTo: Dayjs;
    worktype: number;
}) {
    const dateFromStr = dateFrom?.toISOString();
    const dateToStr = dateTo?.toISOString();

    const { data } = useQuery({
        queryKey: [
            QueryKey.TotalForecastDeltasByDateRangeAndWorktype,
            { dateFrom: dateFromStr, dateTo: dateToStr, worktype },
        ],
        queryFn: () => getTotalForecastDeltaValuesByDateRangeAndWorktype(dateFromStr, dateToStr, worktype),
        enabled: !!dateFrom && !!dateTo && !!worktype,
        initialData: {
            totalForecastDeltas: {
                positive: 0,
                negative: 0,
            },
        },
    });

    return data;
}

export function useAllForecastDeltasByDateRangeWorktypeAndLocation({
    dateFrom,
    dateTo,
    worktype,
    location,
}: {
    dateFrom: Dayjs;
    dateTo: Dayjs;
    worktype: number;
    location: number;
}) {
    const dateFromStr = dateFrom?.toISOString();
    const dateToStr = dateTo?.toISOString();

    const { data } = useQuery({
        queryKey: [
            QueryKey.AllForecastDeltasByDateRangeWorktypeAndLocation,
            { dateFrom: dateFromStr, dateTo: dateToStr, worktype, location },
        ],
        queryFn: () => getAllForecastDeltasByDateRangeWorktypeAndLocation(dateFromStr, dateToStr, worktype, location),
        enabled: !!dateFrom && !!dateTo && !!worktype && !!location,
        initialData: [],
    });

    return [data];
}

export function useTotalForecastDeltasByDateRangeAndWorktypeAndLocation({
    dateFrom,
    dateTo,
    worktype,
    location,
}: {
    dateFrom: Dayjs;
    dateTo: Dayjs;
    worktype: number;
    location: number;
}) {
    const dateFromStr = dateFrom?.toISOString();
    const dateToStr = dateTo?.toISOString();

    const { data } = useQuery({
        queryKey: [
            QueryKey.TotalForecastDeltasByDateRangeAndWorktypeAndLocation,
            { dateFrom: dateFromStr, dateTo: dateToStr, worktype, location },
        ],
        queryFn: () =>
            getSumOfAllForecastDeltaValuesByDateRangeWorktypeAndLocation(dateFromStr, dateToStr, worktype, location),
        enabled: !!dateFrom && !!dateTo && !!worktype && !!location,
        initialData: {
            positive: 0,
            negative: 0,
        },
    });

    return data;
}

export function useAllForecastDeltasByDateRangeAndWorktype({
    dateFrom,
    dateTo,
    worktype,
}: {
    dateFrom: Dayjs;
    dateTo: Dayjs;
    worktype: number;
}) {
    const dateFromStr = dateFrom?.toISOString();
    const dateToStr = dateTo?.toISOString();

    const { data } = useQuery({
        queryKey: [
            QueryKey.AllForecastDeltasByDateRangeAndWorktype,
            { dateFrom: dateFromStr, dateTo: dateToStr, worktype },
        ],
        queryFn: () => getAllForecastDeltasByDateRangeAndWorktype(dateFromStr, dateToStr, worktype),
        enabled: !!dateFrom && !!dateTo && !!worktype,
        initialData: [],
    });

    return [data];
}
